import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { History, Loader2, RefreshCw, User, Users, ShieldCheck, Filter, X } from 'lucide-react';
import api from '@/services/api';

/**
 * AgentHistoryTab — iter128.2 base, iter128.3 filters.
 * Renders /api/chatbots/{chatbot_id}/audit-log so owners can see who
 * (owner / moderator / admin) changed what on this agent. Server-side
 * filters: role, action, date range preset (24h / 7d / 30d / all).
 */
const _roleBadge = (role) => {
  if (role === 'moderator') return { label: 'Moderator', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: Users };
  if (role === 'admin') return { label: 'System Admin', cls: 'bg-violet-100 text-violet-700 border-violet-200', Icon: ShieldCheck };
  return { label: 'Owner', cls: 'bg-blue-100 text-blue-700 border-blue-200', Icon: User };
};

const _actionLabel = (a) => {
  const map = {
    chatbot_update: 'Updated agent settings',
    document_upload: 'Uploaded a document',
    document_delete: 'Deleted a document',
    url_import: 'Imported a URL',
    sitemap_import: 'Imported a sitemap',
  };
  return map[a] || a;
};

const _formatTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d',  label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

const rangeToSince = (v) => {
  const now = Date.now();
  if (v === '24h') return new Date(now - 24 * 60 * 60 * 1000).toISOString();
  if (v === '7d')  return new Date(now - 7  * 24 * 60 * 60 * 1000).toISOString();
  if (v === '30d') return new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
  return null;
};

export const AgentHistoryTab = ({ chatbotId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [range, setRange] = useState('all');

  const params = useMemo(() => {
    const p = { limit: 100 };
    if (roleFilter !== 'all') p.actor_role = roleFilter;
    if (actionFilter !== 'all') p.action = actionFilter;
    const since = rangeToSince(range);
    if (since) p.since = since;
    return p;
  }, [roleFilter, actionFilter, range]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/chatbots/${chatbotId}/audit-log`, { params });
      setEntries(r.data?.entries || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load history');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (chatbotId) load(); }, [chatbotId, params]);

  const filtersActive = roleFilter !== 'all' || actionFilter !== 'all' || range !== 'all';
  const clearFilters = () => { setRoleFilter('all'); setActionFilter('all'); setRange('all'); };

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="agent-history-card">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <History className="w-5 h-5 text-blue-600" />
            Agent history
          </CardTitle>
          <p className="text-sm text-slate-600 mt-1">Recent changes to this agent&apos;s settings and knowledge base, including who made them.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="agent-history-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-md bg-slate-50 border border-slate-200" data-testid="agent-history-filters">
          <Filter className="w-4 h-4 text-slate-500" />
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-8 w-[140px]" data-testid="agent-history-role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="moderator">Moderator</SelectItem>
              <SelectItem value="admin">System Admin</SelectItem>
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="h-8 w-[180px]" data-testid="agent-history-action-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="chatbot_update">Setting changes</SelectItem>
              <SelectItem value="document_upload">Doc uploads</SelectItem>
              <SelectItem value="document_delete">Doc deletions</SelectItem>
              <SelectItem value="url_import">URL imports</SelectItem>
              <SelectItem value="sitemap_import">Sitemap imports</SelectItem>
            </SelectContent>
          </Select>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="h-8 w-[150px]" data-testid="agent-history-range-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filtersActive && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-slate-600" data-testid="agent-history-clear-filters">
              <X className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          )}
          <span className="ml-auto text-xs text-slate-500" data-testid="agent-history-count">{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</span>
        </div>

        {error && (
          <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 mb-3">{error}</div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>
        ) : entries.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500">
            {filtersActive ? 'No entries match the selected filters.' : 'No history yet — changes to this agent will appear here.'}
          </div>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {entries.map((e) => {
              const role = (e.details || {}).actor_role;
              const badge = _roleBadge(role);
              const fields = ((e.details || {}).fields_changed) || [];
              const docName = (e.details || {}).name;
              return (
                <div
                  key={e.log_id}
                  className="flex items-start gap-3 p-3 rounded-md bg-slate-50 border border-slate-200"
                  data-testid={`agent-history-row-${e.log_id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                    <badge.Icon className="w-4 h-4 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-slate-900 text-sm">{_actionLabel(e.action)}</p>
                      <Badge className={`${badge.cls} text-xs`}>{badge.label}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                      by <span className="font-medium">{e.user_email || e.user_id}</span>
                      {' · '}
                      {_formatTime(e.timestamp)}
                    </p>
                    {fields.length > 0 && (
                      <p className="text-xs text-slate-600 mt-1">
                        Fields: <span className="font-mono">{fields.join(', ')}</span>
                      </p>
                    )}
                    {docName && (
                      <p className="text-xs text-slate-600 mt-1">Document: <span className="font-medium">{docName}</span></p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AgentHistoryTab;
